/**
 * Pure H OS · Sprint 1 · Application Shell.
 *
 * Permanent sidebar (nav rail) + fixed top bar with global search,
 * command area, notifications, user profile. Renders route children
 * inside a scrollable content region.
 *
 * Wave A · Amended IA: the sidebar renders from `/config/navigation.ts`
 * with role-based filtering, Favorites, Recent Pages and a Quick Actions
 * strip.
 */
import { ReactNode, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Bell, Search, LogOut, ChevronDown,
  Command as CmdIcon, User as UserIcon, Keyboard,
  Star, StarOff, Zap, Lock, Compass, Cog, Globe, Check,
} from "lucide-react";
import { usePureH } from "../context/PureHContext";
import { usePlatform } from "../context/PlatformContext";
import { TextInput } from "./lib";
import { NAV_DOMAINS, NAV_ITEM_BY_KEY, canSeeDomain, canSeeItem,
  visibleQuickActions, type NavItem, type RoleContext,
} from "../config/navigation";
import { useSidebarPersonalization } from "../hooks/useSidebarPersonalization";
import Breadcrumbs from "./Breadcrumbs";
import { DemoBanner } from "../context/DataModeContext";
import { AICopilot } from "./AICopilot";

function SidebarLink({ item, isFav, onToggleFav }: {
  item: NavItem;
  isFav: boolean;
  onToggleFav: (key: string) => void;
}) {
  const Icon = item.icon;
  const disabled = !!item.locked;
  return (
    <div className="group relative flex items-stretch">
      <NavLink
        to={item.to}
        data-testid={item.testId}
        aria-disabled={disabled || undefined}
        onClick={(e) => { if (disabled) e.preventDefault(); }}
        className={({ isActive }) =>
          "flex-1 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition-colors " +
          (disabled
            ? "text-slate-500 cursor-not-allowed opacity-60"
            : isActive
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-100")
        }
      >
        <Icon size={16} />
        <span className="truncate">{item.label}</span>
        {item.locked ? <Lock size={12} className="ml-auto opacity-70" data-testid={`${item.testId}-lock`} /> : null}
        {item.badge && !item.locked ? (
          <span className="ml-auto text-[9px] uppercase tracking-wide bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">{item.badge}</span>
        ) : null}
      </NavLink>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(item.key); }}
        title={isFav ? "Unpin from Favorites" : "Pin to Favorites"}
        data-testid={`fav-toggle-${item.key}`}
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-slate-500 hover:text-amber-400 px-1"
      >
        {isFav ? <Star size={13} className="fill-amber-400 text-amber-400" /> : <StarOff size={13} />}
      </button>
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 px-3 mt-3 mb-1.5">{label}</div>
  );
}

function Sidebar() {
  const { currentCompany, currentWorkspace, user } = usePureH();
  const { favs, isFav, toggle: toggleFav, recents } = useSidebarPersonalization();
  const navigate = useNavigate();

  const ctx: RoleContext = {
    role: (user?.role || "employee").toLowerCase(),
    isHoldingOwner: !!(user as any)?.isHoldingOwner,
  };

  const favItems = favs.map((k) => NAV_ITEM_BY_KEY[k]).filter(Boolean).filter((it) => canSeeItem(it, ctx));
  const recentItems = recents.map((k) => NAV_ITEM_BY_KEY[k]).filter(Boolean).filter((it) => canSeeItem(it, ctx));
  const quickActions = visibleQuickActions(ctx);

  return (
    <aside
      data-testid="pureh-sidebar"
      className="hidden md:flex md:flex-col w-56 shrink-0 border-r border-slate-200 bg-slate-50/60 h-screen sticky top-0"
    >
      <Link to="/pureh" className="flex items-center gap-2 px-4 h-14 border-b border-slate-200">
        <div className="w-6 h-6 rounded bg-slate-900 text-white flex items-center justify-center text-xs font-bold">P</div>
        <div className="flex flex-col leading-tight">
          <div className="text-sm font-semibold text-slate-900">Pure H OS</div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Enterprise AI</div>
        </div>
      </Link>

      <div className="px-3 py-3 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-1">Company</div>
        <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-company">
          {currentCompany?.name ?? "—"}
        </div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mt-3 mb-1">Workspace</div>
        <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-workspace">
          {currentWorkspace?.name ?? "—"}
        </div>
      </div>

      {/* Quick Actions strip */}
      {quickActions.length > 0 && (
        <div className="px-3 py-2 border-b border-slate-200" data-testid="quick-actions-strip">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-1.5 flex items-center gap-1.5">
            <Zap size={11} /> Quick Actions
          </div>
          <div className="flex flex-wrap gap-1">
            {quickActions.slice(0, 5).map((qa) => (
              <button
                key={qa.key}
                type="button"
                onClick={() => navigate(qa.to)}
                data-testid={qa.testId}
                title={qa.label}
                className="text-[10px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-900 hover:text-white transition-colors flex items-center gap-1"
              >
                <qa.icon size={11} />
                <span className="truncate max-w-[80px]">{qa.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {/* Favorites (only if user has any) */}
        {favItems.length > 0 && (
          <>
            <SectionHeader label="Favorites" />
            <div data-testid="sidebar-favorites">
              {favItems.map((it) => (
                <SidebarLink key={`fav-${it.key}`} item={it} isFav={true} onToggleFav={toggleFav} />
              ))}
            </div>
          </>
        )}

        {/* Recent Pages — removed (P2-2): the same items appear in their domain
             sections below, so listing them twice made the sidebar noisy.
             Use Favorites (star icon) for quick access instead. */}

        {/* Domain sections */}
        {NAV_DOMAINS.filter((d) => canSeeDomain(d, ctx)).map((domain) => {
          const visibleItems = domain.items.filter((it) => canSeeItem(it, ctx));
          if (visibleItems.length === 0) return null;
          return (
            <div key={domain.key} data-testid={`sidebar-domain-${domain.key}`}>
              <SectionHeader label={domain.label} />
              {visibleItems.map((it) => (
                <SidebarLink key={it.key} item={it} isFav={isFav(it.key)} onToggleFav={toggleFav} />
              ))}
            </div>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-200 text-[10px] text-slate-400 leading-tight">
        <div>v1.0 · Wave C/D/E</div>
        <div>{ctx.role}{ctx.isHoldingOwner ? " · holding" : ""}</div>
      </div>
    </aside>
  );
}

function MarketSelector() {
  const { markets, currentMarket, setMarket } = usePureH();
  const [open, setOpen] = useState(false);
  if (markets.length === 0) return null;
  return (
    <div className="relative">
      <button
        type="button"
        data-testid="market-selector"
        onClick={() => setOpen((o) => !o)}
        title="Active market — filters every dashboard, KPI, order and financial figure"
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded hover:bg-slate-50"
      >
        <Globe size={13} />
        <span className="hidden sm:inline">{currentMarket?.name ?? "No market"}</span>
        <span
          data-testid="active-currency-badge"
          className="text-[10px] font-bold uppercase tracking-wide bg-slate-900 text-white rounded px-1.5 py-0.5"
        >
          {currentMarket?.currency ?? "—"}
        </span>
        <ChevronDown size={12} className="text-slate-400" />
      </button>
      {open && (
        <div
          data-testid="market-menu"
          className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg py-1 z-30"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-slate-400">Market</div>
          {markets.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`market-option-${m.id}`}
              onClick={() => { setMarket(m.id); setOpen(false); }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <span className="flex items-center gap-2">
                {m.id === currentMarket?.id
                  ? <Check size={13} className="text-slate-900" />
                  : <span className="w-[13px]" />}
                {m.name}
              </span>
              <span className="text-[10px] font-bold uppercase text-slate-500">{m.currency}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Topbar() {
  const [openMenu, setOpenMenu] = useState<null | "user">(null);
  const { user, logout } = usePureH();
  const { openPalette, toggleDrawer, unreadCount } = usePlatform();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate("/pureh/login", { replace: true }); };

  return (
    <header
      data-testid="pureh-topbar"
      className="sticky top-0 z-20 flex items-center gap-4 h-14 px-4 md:px-6 bg-white/95 backdrop-blur border-b border-slate-200"
    >
      {/* Search — clicking opens the Command Palette */}
      <div className="flex-1 max-w-2xl relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <button
          type="button"
          onClick={openPalette}
          data-testid="global-search-input"
          className="w-full pl-8 pr-16 py-1.5 text-sm bg-slate-100/70 border border-transparent rounded text-left"
          style={{ color: "var(--ph-ink-muted)" }}
        >
          Search across everything…
        </button>
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-white border border-slate-200 rounded">
          <CmdIcon size={10} /> K
        </kbd>
      </div>

      {/* Market scope selector (system-wide dimension) */}
      <MarketSelector />

      {/* Command area */}
      <button
        data-testid="command-area-btn"
        onClick={openPalette}
        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded hover:bg-slate-50"
      >
        <Compass size={13} />
        Command
      </button>

      {/* Notifications — opens the global drawer */}
      <div className="relative">
        <button
          data-testid="notifications-btn"
          onClick={toggleDrawer}
          className="relative w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100"
        >
          <Bell size={16} className="text-slate-600" />
          {unreadCount > 0 && (
            <span
              data-testid="notif-unread-dot"
              className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--ph-danger)" }}
            />
          )}
        </button>
      </div>

      {/* User profile */}
      <div className="relative">
        <button
          data-testid="user-profile-btn"
          onClick={() => setOpenMenu(openMenu === "user" ? null : "user")}
          className="flex items-center gap-2 pl-1 pr-2 py-1 rounded hover:bg-slate-100"
        >
          <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold">
            {user?.avatarInitials ?? "?"}
          </div>
          <div className="hidden md:flex flex-col leading-tight text-left">
            <div className="text-[12px] font-semibold text-slate-900">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</div>
          </div>
          <ChevronDown size={12} className="text-slate-400" />
        </button>
        {openMenu === "user" && (
          <div
            data-testid="user-menu"
            className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg py-1"
          >
            <div className="px-3 py-2 border-b border-slate-100">
              <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => { setOpenMenu(null); navigate("/pureh/profile"); }}
              data-testid="menu-profile"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <UserIcon size={14} /> Profile
            </button>
            <button
              onClick={() => { setOpenMenu(null); navigate("/pureh/settings"); }}
              data-testid="menu-settings"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <Cog size={14} /> Settings
            </button>
            <button
              onClick={() => { setOpenMenu(null); navigate("/pureh/shortcuts"); }}
              data-testid="menu-shortcuts"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <Keyboard size={14} /> Keyboard shortcuts
            </button>
            <div className="my-1 border-t border-slate-100" />
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900" data-testid="pureh-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <DemoBanner />
        <Topbar />
        <div className="border-b border-slate-100 bg-white/70 px-4 md:px-6 lg:px-8 py-2">
          <Breadcrumbs />
        </div>
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-[1440px] mx-auto p-4 md:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
      <AICopilot />
    </div>
  );
}
